import React from 'react';
import { ArrowRight, CreditCard, Building2, Landmark, Cloud } from 'lucide-react';

const platforms = [
  {
    id: 'caribbean-payments-alliance',
    name: 'Caribbean Payments Alliance',
    tagline: 'Payments',
    description: 'A payment orchestration platform enabling businesses to accept and route payments across multiple banks and digital wallets.',
    icon: CreditCard,
    url: 'https://caribbeanpaymentsalliance.com',
    logo: '/logos/CPALogo.png',
    accent: '#C9A227',
  },
  {
    id: 'apc-estate-flow',
    name: 'APC Estate Flow',
    tagline: 'Real Estate',
    description: 'A real estate and financing platform connecting property listings, transactions, and payment solutions into one seamless ecosystem.',
    icon: Building2,
    url: 'https://apcestateflow.com',
    logo: '/logos/APCEstateFlow.png',
    accent: '#1e3a5f',
  },
  {
    id: 'bahamas-pay',
    name: 'Bahamas Pay',
    tagline: 'Financial Services',
    description: 'A financial services platform designed for business incorporation, licensing, and payment enablement in The Bahamas.',
    icon: Landmark,
    url: 'https://bahamaspay.com',
    logo: '/logos/BahamasPay.png',
    accent: '#1e3a5f',
  },
  {
    id: 'cloudsync-pro',
    name: 'CloudSync Pro',
    tagline: 'Automation',
    description: 'Automation and integration platform connecting systems, data, and workflows across your digital infrastructure.',
    icon: Cloud,
    url: 'https://csyncpro.com/',
    logo: '/logos/CloudSyncPro.png',
    accent: '#C9A227',
  },
];

const DigitalPlatforms = () => {
  return (
    <section
      className="py-20 md:py-28 bg-white"
      data-testid="digital-platforms-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="text-center mb-6">
          <span className="text-[#C9A227] text-xs tracking-[0.3em] uppercase block mb-4">
            SAG Digital Platforms
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-light text-black mb-6 leading-tight"
            data-testid="digital-platforms-title"
          >
            Our Digital Platforms
          </h2>
          <p className="text-gray-500 text-base md:text-lg max-w-3xl mx-auto">
            An integrated ecosystem of digital platforms powering payments, commerce,
            and financial infrastructure globally.
          </p>
        </div>

        {/* Gold divider */}
        <div className="w-20 h-[2px] bg-gradient-to-r from-transparent via-[#C9A227] to-transparent mx-auto mb-16" />

        {/* 4-Card Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {platforms.map((platform) => {
            const Icon = platform.icon;
            const isGold = platform.accent === '#C9A227';

            return (
              <a
                key={platform.id}
                href={platform.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative flex flex-col bg-[#fafafa] border border-gray-200 p-8 transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_12px_40px_-12px_rgba(0,0,0,0.12)] hover:border-[#C9A227]/40 cursor-pointer"
                data-testid={`digital-platform-card-${platform.id}`}
              >
                {/* Top accent line on hover */}
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#C9A227] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />

                {/* Logo or Icon */}
                <div className="mb-6 h-16 flex items-center">
                  {platform.logo ? (
                    <img
                      src={platform.logo}
                      alt={platform.name}
                      className="h-14 w-auto object-contain max-w-[160px]"
                      data-testid={`platform-logo-${platform.id}`}
                    />
                  ) : (
                    <div
                      className={`w-14 h-14 flex items-center justify-center ${
                        isGold ? 'bg-[#C9A227]/8' : 'bg-[#1e3a5f]/8'
                      }`}
                      style={{
                        backgroundColor: isGold
                          ? 'rgba(201,162,39,0.08)'
                          : 'rgba(30,58,95,0.08)',
                      }}
                    >
                      <Icon
                        className={`w-7 h-7 ${
                          isGold ? 'text-[#C9A227]' : 'text-[#1e3a5f]'
                        }`}
                      />
                    </div>
                  )}
                </div>

                {/* Tagline */}
                <span
                  className={`text-[10px] tracking-[0.2em] uppercase font-medium mb-3 block ${
                    isGold ? 'text-[#C9A227]' : 'text-[#1e3a5f]'
                  }`}
                >
                  {platform.tagline}
                </span>

                {/* Platform Name */}
                <h3
                  className="text-lg font-medium text-black mb-3 group-hover:text-[#1e3a5f] transition-colors leading-snug"
                  data-testid={`platform-name-${platform.id}`}
                >
                  {platform.name}
                </h3>

                {/* Description */}
                <p className="text-gray-500 text-sm leading-relaxed mb-6 flex-1">
                  {platform.description}
                </p>

                {/* CTA */}
                <div
                  className={`flex items-center gap-2 text-xs tracking-[0.15em] uppercase font-medium ${
                    isGold ? 'text-[#C9A227]' : 'text-[#1e3a5f]'
                  } group-hover:text-[#C9A227] transition-colors mt-auto`}
                >
                  <span>Visit Platform</span>
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </div>
              </a>
            );
          })}
        </div>

        {/* "How They Work Together" Strip */}
        <div
          className="mt-16 relative overflow-hidden border border-gray-200 bg-[#fafafa]"
          data-testid="platforms-ecosystem-strip"
        >
          <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#C9A227]" />
          <div className="px-8 py-8 md:py-10 md:px-12 flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="flex-1">
              <span className="text-[#C9A227] text-[10px] tracking-[0.2em] uppercase font-medium block mb-2">
                Integrated Ecosystem
              </span>
              <p className="text-black text-base md:text-lg font-light leading-relaxed">
                Together, these platforms form the Sebastian Alliance ecosystem —
                delivering end-to-end digital infrastructure from payments to
                operations.
              </p>
            </div>
            <a
              href="#ecosystem"
              className="inline-flex items-center gap-2 px-6 py-3 bg-black text-white text-xs tracking-[0.15em] uppercase font-medium hover:bg-[#C9A227] hover:text-black transition-all duration-300 whitespace-nowrap"
              data-testid="explore-ecosystem-cta"
            >
              Explore Ecosystem
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DigitalPlatforms;
